import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, Button, Typography, Space, Divider, Tag } from 'antd';
import { 
    ClockCircleOutlined, 
    CheckCircleOutlined, 
    TruckOutlined,
    FireOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import { useOrderTracking } from '../../contexts/OrderTrackingContext';

const { Text } = Typography;

const OrderStatusIndicator = () => {
    const { getActiveOrders, getStatusDisplayInfo } = useOrderTracking();
    const [activeOrders, setActiveOrders] = useState([]);

    useEffect(() => {
        const orders = getActiveOrders();
        setActiveOrders(orders);
    }, [getActiveOrders]);

    if (activeOrders.length === 0) {
        return null;
    }

    // Get the most recent active order
    const recentOrder = activeOrders[0];
    const statusInfo = getStatusDisplayInfo(recentOrder.status);

    const getStatusIcon = (status) => {
        const iconMap = {
            'pending': <ClockCircleOutlined />,
            'confirmed': <CheckCircleOutlined />,
            'preparing': <FireOutlined />,
            'ready': <CheckCircleOutlined />,
            'out_for_delivery': <TruckOutlined />,
            'delivered': <CheckCircleOutlined />,
            'cancelled': <CloseCircleOutlined />
        };
        return iconMap[status] || <ClockCircleOutlined />;
    };

    const dropdownMenu = {
        items: [
            {
                key: 'header',
                label: (
                    <div className="px-2 py-1">
                        <Text strong>Active Orders ({activeOrders.length})</Text>
                    </div>
                ),
                type: 'group'
            },
            ...activeOrders.map((order, index) => ({
                key: order.id,
                label: (
                    <div className="px-2 py-2 min-w-64">
                        <div className="flex items-center justify-between mb-1">
                            <Text strong>Order #{order.order_number}</Text>
                            <Tag color={statusInfo.color} size="small">
                                {statusInfo.label}
                            </Tag>
                        </div>
                        <div className="flex items-center gap-2">
                            {getStatusIcon(order.status)}
                            <Text type="secondary" className="text-xs">
                                {order.vendor?.business_name}
                            </Text>
                        </div>
                        {order.estimated_delivery_time && (
                            <div className="mt-1">
                                <Text type="secondary" className="text-xs">
                                    ETA: {new Date(order.estimated_delivery_time).toLocaleTimeString()}
                                </Text>
                            </div>
                        )}
                    </div>
                )
            })),
            {
                key: 'divider',
                type: 'divider'
            },
            {
                key: 'view-all',
                label: (
                    <div className="px-2 py-1">
                        <Button type="link" size="small" href="/order-tracking">
                            View All Orders
                        </Button>
                    </div>
                )
            }
        ]
    };

    return (
        <Dropdown
            menu={dropdownMenu}
            placement="bottomRight"
            trigger={['click']}
        >
            <Badge 
                count={activeOrders.length} 
                size="small"
                offset={[5, -5]}
            >
                <Button
                    type="text"
                    icon={getStatusIcon(recentOrder.status)}
                    className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
                >
                    <span className="hidden sm:inline">
                        {statusInfo.label}
                    </span>
                </Button>
            </Badge>
        </Dropdown>
    );
};

export default OrderStatusIndicator;
